/**
 * aC Men's Hub のコーデ生成キャパシティ API contract（zod schema + 型 + 算出ロジック）。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Shares the coorde generation capacity contract between API and Web, and owns the single implementation of the per-season upper bound calculation.
 * @graph-connects none
 */
import { z } from 'zod';
import { SeasonSchema } from './item-catalog.js';
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Enumerates the three garment layers a generated outfit is composed of.
 * @graph-connects none
 */
export const CoordeLayerSchema = z.enum(['tops', 'bottoms', 'outer']);
/**
 * phoenix_item のカテゴリ名からコーデのレイヤーへの対応。
 * 区分は情報設計書「2.2 レイヤー分類」が SoT。メンズの衣類カテゴリは6種類のみで、
 * 小物カテゴリは存在しない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Maps a phoenix_item clothing category name onto the outfit layer it can occupy.
 * @graph-connects none
 */
export const COORDE_CATEGORY_TO_LAYER = {
    カットソー: 'tops',
    シャツ: 'tops',
    ニット: 'tops',
    パンツ: 'bottoms',
    ジャケット: 'outer',
    ライトアウター: 'outer',
};
/**
 * 1ラン内で同じ FKU を使い回してよい上限。素材の多様性を確保するためのビジネスルール（BR-3）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Caps how many times one FKU may appear across a single generation run.
 * @graph-connects none
 */
export const COORDE_REUSE_LIMIT = 2;
/**
 * 1日に生成してよいコーデ数の上限。画像生成は従量課金のため請求保護として設ける（BR-8）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Caps how many outfits may be generated per day to bound image generation spend.
 * @graph-connects none
 */
export const COORDE_DAILY_LIMIT = 30;
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Names which constraint binds the upper bound, so the UI can explain the number instead of just showing it.
 * @graph-connects none
 */
export const CoordeCapacityLimitedBySchema = z.enum(['tops', 'bottoms', 'dailyLimit']);
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Counts the FKUs actually usable for generation, per layer.
 * @graph-connects none
 */
export const CoordeLayerStockSchema = z.object({
    tops: z.number(),
    bottoms: z.number(),
    outer: z.number(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Reports one season's usable stock, its generation ceiling and which constraint produced that ceiling.
 * @graph-connects none
 */
export const CoordeSeasonCapacitySchema = z.object({
    season: SeasonSchema,
    usableFku: CoordeLayerStockSchema,
    maxOutfits: z.number(),
    limitedBy: CoordeCapacityLimitedBySchema,
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Response of the coorde capacity endpoint: every season's ceiling plus the rule constants that produced them.
 * @graph-connects none
 */
export const CoordeCapacityResponseSchema = z.object({
    seasons: z.array(CoordeSeasonCapacitySchema),
    reuseLimit: z.number(),
    dailyLimit: z.number(),
    snapshotAt: z.string(),
});
/**
 * 使用可能 FKU 数から、その季節で生成してよいコーデ数の上限を求める。
 *
 * 上限 = min(トップス数 × 再利用上限, ボトムス数 × 再利用上限, 日次上限)。
 * アウターは 2 点コーデでは使わないため上限計算には含めない（3 点コーデでも
 * トップス・ボトムスが先に枯れるため、束縛条件になりえない）。
 *
 * `limitedBy` はトップス → ボトムス → 日次上限の順に判定する。複数が同値で並んだ
 * ときに在庫側を優先するのは、在庫は仕入で動かせる＝利用者が取れる行動があるため。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Derives how many outfits one season can yield without exceeding the per-FKU reuse limit or the daily spend cap.
 * @graph-connects none
 */
export function calculateCoordeCapacity(usableFku, reuseLimit, dailyLimit) {
    const byTops = usableFku.tops * reuseLimit;
    const byBottoms = usableFku.bottoms * reuseLimit;
    const maxOutfits = Math.min(byTops, byBottoms, dailyLimit);
    if (maxOutfits === byTops)
        return { maxOutfits, limitedBy: 'tops' };
    if (maxOutfits === byBottoms)
        return { maxOutfits, limitedBy: 'bottoms' };
    return { maxOutfits, limitedBy: 'dailyLimit' };
}
//# sourceMappingURL=coorde-capacity.js.map